/**
 * AdminEmailsPage
 * Owner-only export of participant emails for outreach — pick a cohort, copy the
 * addresses or download a CSV ready for a mailing-list import.
 *
 * These addresses were given so someone could submit a drawing or claim a name, not to
 * receive a newsletter, so the page says so plainly: invite people to subscribe rather
 * than subscribing them.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Copy, Download, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getParticipantEmails, type EmailCohort, type Participant } from '@/services/supabase-storage';

const MONO = { fontFamily: 'FK Grotesk Mono, monospace' } as const;

const COHORTS: { key: EmailCohort; label: string; blurb: string }[] = [
  { key: 'accepted', label: 'Zine — accepted', blurb: 'Artists who said yes to being printed.' },
  { key: 'invited', label: 'Zine — invited', blurb: 'Everyone asked about the zine, including no reply yet.' },
  { key: 'artists', label: 'All artists', blurb: 'Everyone with a drawing in the gallery.' },
  { key: 'prompters', label: 'Prompt writers', blurb: 'Everyone who submitted a prompt.' },
];

function fmtDate(raw?: string | null): string {
  if (!raw) return '—';
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AdminEmailsPage() {
  const [cohort, setCohort] = useState<EmailCohort>('accepted');
  const [people, setPeople] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (c: EmailCohort) => {
    setLoading(true);
    try {
      setError(null);
      setPeople(await getParticipantEmails(c));
    } catch (e: any) {
      setError(e?.message || 'Could not load the list.');
      setPeople([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(cohort);
  }, [cohort, load]);

  const copyEmails = useCallback(async () => {
    const text = people.map((p) => p.email).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success(`${people.length} email${people.length === 1 ? '' : 's'} copied.`);
    } catch {
      toast.error('Could not copy — try the CSV instead.');
    }
  }, [people]);

  const downloadCsv = useCallback(() => {
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      'email,name,drawings,last_activity',
      ...people.map((p) => [p.email, p.name ?? '', p.drawings, p.at ?? ''].map(cell).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `promptbrush-${cohort}-emails.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [people, cohort]);

  const active = COHORTS.find((c) => c.key === cohort);

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-medium" style={MONO}>Emails</h1>
        <button
          onClick={() => load(cohort)}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          style={MONO}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="text-muted-foreground/70 text-xs mb-6" style={MONO}>
        People gave these addresses to submit a drawing or claim a name — not to get a newsletter.
        Inviting them to subscribe keeps you on the right side of that (and out of spam folders).
      </p>

      <div className="flex flex-wrap gap-2 mb-2">
        {COHORTS.map((c) => (
          <button
            key={c.key}
            onClick={() => setCohort(c.key)}
            className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
              cohort === c.key
                ? 'bg-foreground text-background border-foreground'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
            style={MONO}
          >
            {c.label}
          </button>
        ))}
      </div>
      {active && (
        <p className="text-muted-foreground/60 text-[11px] mb-6" style={MONO}>{active.blurb}</p>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-3" />
          <p className="text-muted-foreground text-sm" style={MONO}>Loading…</p>
        </div>
      ) : error ? (
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <p className="text-muted-foreground mb-3" style={MONO}>{error}</p>
          <button onClick={() => load(cohort)} className="text-sm text-foreground underline" style={MONO}>Try again</button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-foreground text-sm" style={MONO}>
              {people.length} {people.length === 1 ? 'person' : 'people'}
            </span>
            <div className="flex-1" />
            <button
              onClick={copyEmails}
              disabled={people.length === 0}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs border border-border text-foreground hover:bg-accent transition-colors disabled:opacity-50"
              style={MONO}
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy emails</>}
            </button>
            <button
              onClick={downloadCsv}
              disabled={people.length === 0}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50"
              style={MONO}
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>

          {people.length === 0 ? (
            <p className="text-muted-foreground text-sm" style={MONO}>Nobody in this group yet.</p>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-sm" style={MONO}>
                <thead>
                  <tr className="border-b border-border text-muted-foreground/70 text-left text-xs">
                    <th className="px-4 py-3 font-normal">Email</th>
                    <th className="px-4 py-3 font-normal">Name</th>
                    <th className="px-4 py-3 font-normal text-center">Drawings</th>
                    <th className="px-4 py-3 font-normal text-right">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.email} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-2.5 text-foreground">{p.email}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{p.name || '—'}</td>
                      <td className="px-4 py-2.5 text-center text-muted-foreground">{p.drawings}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground/70">{fmtDate(p.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminEmailsPage;
